import { axiosClient } from "@lib/services/axios";
import { ENDPOINTS } from "@lib/constants/general";
import {
  CreateBulkDriverResponse,
  DeleteDriverResponse,
  Driver,
  DriverResponse,
} from "@lib/types/driver";
import { z } from "zod";

// Zod schema for form validation
export const driverBulkSchema = z.object({
  file: z
    .custom<File>(
      (value) => value instanceof File, // Ensure the value is a File instance
      { message: "A valid file is required." }
    )
    .refine(
      (file) => file.type === "text/csv", // Only allow CSV MIME type
      {
        message: "The file must be a CSV file.",
      }
    ),
});

export type DriverBulkFormData = z.infer<typeof driverBulkSchema>;

export const driverSchema = z.object({
  name: z.string().min(1, "Name is required"),
  code: z.string().min(16, "Minimum is 16 characters"),
  description: z.string().optional(),
  phone: z
    .string()
    .optional()
    .refine((value) => !value || (value.length >= 7 && value.length <= 16), {
      message: "Phone number must be between 7 and 16 characters",
    }),
  mobile_key: z.string().optional(),
  exclusive: z.boolean().optional(),
  parameter_number: z
    .string()
    .min(1, "Parameter number is required")
    .refine((val) => !isNaN(Number(val)), {
      message: "Must be a valid number",
    }),
  custom_fields: z
    .array(
      z.object({
        key: z.string().min(1, "Key is required"),
        value: z.string().min(1, "Value is required"),
      })
    )
    .optional(),
});
export type DriverFormData = z.infer<typeof driverSchema>;

export const updateDriverSchema = z.object({
  name: z.string().min(1, "Name is required"),
  code: z.string().min(16, "Minimum is 16 characters"),
  description: z.string().optional(),
  phone: z
    .string()
    .optional()
    .refine((value) => !value || (value.length >= 7 && value.length <= 16), {
      message: "Phone number must be between 7 and 16 characters",
    }),
  mobile_key: z.string().optional(),
  exclusive: z.boolean().optional(),
  custom_fields: z
    .array(
      z.object({
        key: z.string().min(1, "Key is required"),
        value: z.string().min(1, "Value is required"),
      })
    )
    .optional(),
});
export type UpdateDriverFormData = z.infer<typeof updateDriverSchema>;

export const createDriver = async (
  createDriverParams: DriverFormData
): Promise<DriverResponse> => {
  const { data } = await axiosClient.post(
    ENDPOINTS.BASE_DRIVER,
    createDriverParams
  );
  return data;
};

export const syncDriver = async (
  syncDriverParams: DriverFormData,
  wialonDriverId: number
): Promise<DriverResponse> => {
  const { data } = await axiosClient.post(
    `${ENDPOINTS.BASE_DRIVER}/sync/${wialonDriverId}`,
    syncDriverParams
  );
  return data;
};

export const createBulkDriver = async (
  createBulkDriverParams: DriverBulkFormData
): Promise<CreateBulkDriverResponse> => {
  const formData = new FormData();
  formData.append("file", createBulkDriverParams.file);

  const { data } = await axiosClient.post(
    `${ENDPOINTS.BASE_DRIVER}/bulk-upload`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return data;
};

export const updateDriver = async ({
  driverId,
  updateDriverParams,
}: {
  driverId: string;
  updateDriverParams: UpdateDriverFormData;
}): Promise<DriverResponse> => {
  const { data } = await axiosClient.put(
    `${ENDPOINTS.BASE_DRIVER}/${driverId}`,
    updateDriverParams
  );
  return data;
};

export const deleteDriver = async (
  driverId: string
): Promise<DeleteDriverResponse> => {
  const { data } = await axiosClient.delete(
    `${ENDPOINTS.BASE_DRIVER}/${driverId}`
  );

  return data;
};
